import type { Texture } from "three/webgpu";
import type { Color } from "../../common/color.js";
import type { Point, Rect } from "../../common/geometry.js";
import type { DrawableTexture } from "../../engine/texture.js";
import { rectIntersect } from "../../common/geometry.js";
import type { FontSpec } from "./GlyphAtlas.js";
import { GlyphAtlas } from "./GlyphAtlas.js";
import type { GpuContext } from "../../engine/GpuContext.js";
import {
  MODE_ARC,
  MODE_CAPSULE,
  MODE_CHECKER,
  MODE_DIAMOND,
  MODE_RADIAL,
  MODE_GLOW,
  MODE_POLY,
  MODE_SHINE,
  MODE_TRIANGLE,
  MODE_GRADIENT,
  MODE_IMAGE,
  MODE_IMAGE_PREMULTIPLIED,
  QuadBatcher,
  type SurfaceSize,
} from "./QuadBatcher.js";
import {
  codePointAt,
  quantizeRasterSize,
  TextLayout,
  wrapLines,
  wrapSpanRuns,
  type TextMetrics,
} from "./TextLayout.js";

/** A translate + uniform-scale transform. Rotation is deliberately out of scope. */
export interface Transform {
  scale: number;
  tx: number;
  ty: number;
}

export interface RectStyle {
  fill?: Color;
  /** Corner radius in *local* (pre-transform) pixels. */
  radius?: number;
  /** Border width in local pixels; drawn inset from the edge. */
  borderWidth?: number;
  borderColor?: Color;
}


export type { DrawableTexture };

const IDENTITY: Transform = { scale: 1, tx: 0, ty: 0 };
const WHITE_TINT = { r: 1, g: 1, b: 1, a: 1 } as const;

/** Vertical anchor for drawText's `y` argument. */
export type TextBaseline = "alphabetic" | "top" | "middle" | "bottom";

export interface TextOptions {
  baseline?: TextBaseline;
  /** Truncate with an ellipsis past this logical width. */
  maxWidth?: number;
}

/** Which way a triangle points. */
export type Direction = "up" | "down" | "left" | "right";

/** How a triangle or a diamond is drawn. */
export interface ShapeStyle {
  /** Radians, clockwise about the shape's center. */
  rotation?: number;
  /** When > 0 the shape is an outline of this thickness rather than a fill. */
  border?: number;
  /** Defaults to the shape's own color, so one argument outlines it. */
  borderColor?: Color;
}

/** Invisible, so an outlined shape shows only its ring. */
const TRANSPARENT_FILL: Color = { r: 0, g: 0, b: 0, a: 0 };

/**
 * The border half of a shape's style.
 *
 * The fragment shader's ring is generic across every distance-field mode — it
 * is the band between the silhouette and the silhouette pushed inward — so a
 * triangle and a diamond get an outline for free, from the same code that
 * outlines a rounded rect.
 */
function borderOf(
  options: ShapeStyle,
  color: Color,
  scale: number,
): { borderWidth?: number; borderColor?: Color } {
  if (!options.border || options.border <= 0) return {};
  return { borderWidth: options.border * scale, borderColor: options.borderColor ?? color };
}

/** Must match the branch order in the shader's MODE_TRIANGLE. */
const DIRECTION_INDEX: Record<Direction, number> = { up: 0, down: 1, left: 2, right: 3 };


/**
 * One styled run inside a line of rich text.
 *
 * A span with a `draw` callback is *atomic*: it reserves `width` logical
 * pixels, is handed the box it may paint in, and is never split by wrapping.
 * That is how an inline icon, a rarity gem or a key cap gets into a line
 * without the renderer having to know what any of those are.
 */
export interface TextSpan {
  /** Empty for an atomic span. */
  text: string;
  /** Falls back to the color passed to the draw call. */
  color?: Color;
  /** Falls back to the font passed to the draw call. */
  font?: FontSpec;
  /** Advance of an atomic span, in local px. Ignored when `text` is set. */
  width?: number;
  /** Paints an atomic span into the line box reserved for it. */
  draw?: (rc: RenderContext, box: Rect) => void;
}
/**
 * What a control offers as a tooltip: a plain string, or styled spans.
 *
 * Lives here rather than in core/Tooltip so `Control` can name it without
 * importing the view that renders it.
 */
export type TooltipContent = string | readonly TextSpan[];

export interface TextBlockOptions {
  /** Horizontal placement of each line within the rect. */
  align?: "left" | "center" | "right";
  /** Vertical placement of the block as a whole. */
  verticalAlign?: "top" | "middle" | "bottom";
  /** Baseline-to-baseline distance. Defaults to `lineHeight(font)`. */
  lineHeight?: number;
  /** Drop lines past this many, ellipsizing the last one kept. */
  maxLines?: number;
}

/**
 * Baseline-to-baseline distance as a multiple of the font's ascent plus
 * descent. Roughly what a browser picks for `normal`, and the single place
 * the framework decides it: tooltips, message boxes and wrapped labels all
 * used to carry their own slightly different fudge factor.
 */
const LINE_SPACING = 1.3;

/**
 * The drawing surface handed to controls and themes during a paint pass.
 *
 * All coordinates passed in are *local* to the current transform. The context
 * maps them to screen space itself, so a ScrollViewer can push a
 * translate+scale and its children stay oblivious.
 */
export class RenderContext {
  readonly gpu: GpuContext;
  readonly batcher: QuadBatcher;
  readonly atlas: GlyphAtlas;
  readonly text: TextLayout;

  #transforms: Transform[] = [IDENTITY];
  #clips: Rect[] = [];
  #opacities: number[] = [1];

  constructor(gpu: GpuContext, batcher: QuadBatcher, atlas?: GlyphAtlas) {
    this.gpu = gpu;
    this.batcher = batcher;
    this.atlas = atlas ?? new GlyphAtlas(gpu);
    this.text = new TextLayout(this.atlas);
  }

  /**
   * Reset stacks for a new frame; the clip becomes the full canvas - or the whole of `surface`,
   * for a frame painted off-screen into a layer (`PaintLayer`).
   */
  begin(surface?: SurfaceSize & { width: number; height: number }): void {
    this.#transforms.length = 1;
    this.#transforms[0] = IDENTITY;
    const { width, height } = surface ?? this.gpu.size;
    this.#clips.length = 0;
    this.#clips.push({ x: 0, y: 0, width, height });
    this.#opacities.length = 1;
    this.#opacities[0] = 1;
    this.batcher.begin(surface);
    this.batcher.setScissor(this.#clips[0]!);
  }

  /** Upload the frame's new glyphs and draw its quads into whatever the renderer is pointed at. */
  flush(): void {
    this.atlas.upload();
    this.batcher.flush();
  }

  get transform(): Transform {
    return this.#transforms[this.#transforms.length - 1]!;
  }

  /** Current clip in screen coordinates. */
  get clip(): Rect {
    return this.#clips[this.#clips.length - 1]!;
  }

  /** Effective scale, i.e. local px -> screen px. */
  get scale(): number {
    return this.transform.scale;
  }

  /** True when nothing drawn now could be visible. */
  get isClippedOut(): boolean {
    const c = this.clip;
    return c.width <= 0 || c.height <= 0;
  }

  // --- transforms -------------------------------------------------------

  /** Compose a translate + uniform scale on top of the current transform. */
  pushTransform(tx: number, ty: number, scale = 1): void {
    const cur = this.transform;
    this.#transforms.push({
      scale: cur.scale * scale,
      tx: cur.tx + tx * cur.scale,
      ty: cur.ty + ty * cur.scale,
    });
  }

  popTransform(): void {
    if (this.#transforms.length > 1) this.#transforms.pop();
  }

  /** Local point -> screen point. */
  toScreen(p: Point): Point {
    const t = this.transform;
    return { x: p.x * t.scale + t.tx, y: p.y * t.scale + t.ty };
  }

  /** Local rect -> screen rect. */
  rectToScreen(r: Rect): Rect {
    const t = this.transform;
    return {
      x: r.x * t.scale + t.tx,
      y: r.y * t.scale + t.ty,
      width: r.width * t.scale,
      height: r.height * t.scale,
    };
  }

  /** Screen point -> local point (for hit-testing inside a transform). */
  toLocal(p: Point): Point {
    const t = this.transform;
    return { x: (p.x - t.tx) / t.scale, y: (p.y - t.ty) / t.scale };
  }

  // --- opacity ----------------------------------------------------------

  /** How opaque anything drawn now comes out, 0..1: every `pushOpacity` in force, multiplied. */
  get opacity(): number {
    return this.#opacities[this.#opacities.length - 1]!;
  }

  /**
   * Fade everything drawn until the matching `popOpacity` by `alpha`, on top of any fade already
   * in force.
   *
   * Applied per quad rather than by compositing a layer, so overlapping shapes inside a faded
   * subtree show through each other where a composited fade would not. That is the price of
   * keeping one pass and no off-screen target, and at the fades this exists for - an entrance, a
   * cross-fade - nobody can see it.
   */
  pushOpacity(alpha: number): void {
    const a = Number.isFinite(alpha) ? Math.min(1, Math.max(0, alpha)) : 1;
    const next = this.opacity * a;
    this.#opacities.push(next);
    this.batcher.alpha = next;
  }

  popOpacity(): void {
    if (this.#opacities.length > 1) {
      this.#opacities.pop();
      this.batcher.alpha = this.opacity;
    }
  }

  // --- clipping ---------------------------------------------------------

  /** Intersect the clip with `r` (local coords). Always pair with popClip(). */
  pushClip(r: Rect): void {
    const next = rectIntersect(this.clip, this.rectToScreen(r));
    this.#clips.push(next);
    this.batcher.setScissor(next);
  }

  popClip(): void {
    if (this.#clips.length > 1) {
      this.#clips.pop();
      this.batcher.setScissor(this.clip);
    }
  }

  /** Convenience: run `body` with an extra clip applied. */
  clipped(r: Rect, body: () => void): void {
    this.pushClip(r);
    if (!this.isClippedOut) body();
    this.popClip();
  }

  // --- primitives -------------------------------------------------------

  drawRect(r: Rect, style: RectStyle): void {
    const s = this.transform.scale;
    this.batcher.push(this.rectToScreen(r), {
      fill: style.fill,
      cornerRadius: (style.radius ?? 0) * s,
      borderWidth: (style.borderWidth ?? 0) * s,
      borderColor: style.borderColor,
    });
  }

  /** Filled rect, no border. */
  fillRect(r: Rect, fill: Color, radius = 0): void {
    this.drawRect(r, { fill, radius });
  }

  /**
   * Fill every other local pixel of `r`, the ones where `x + y` is even: a
   * dotted focus ring, a tree guide or a checker fill, in one quad. Drawn a
   * pixel at a time those cost a quad per dot, and a focus ring around a wide
   * list row was a thousand of them.
   */
  fillChecker(r: Rect, color: Color): void {
    const s = this.transform.scale;
    this.batcher.push(this.rectToScreen(r), {
      fill: color,
      mode: MODE_CHECKER,
      uv: [s, (Math.floor(r.x) + Math.floor(r.y)) & 1, 0, 0],
    });
  }

  /**
   * A 1-device-pixel hairline rect outline. Used by Classic bevels and focus
   * rings where the line must not thin out under fractional DPR.
   */
  strokeRect(r: Rect, color: Color, width = 1, radius = 0): void {
    this.drawRect(r, { borderWidth: width, borderColor: color, radius });
  }

  /**
   * A stroked line from `a` to `b`, with round caps.
   *
   * The one primitive that is not axis-aligned. Everything else in this
   * renderer is a rectangle, so a diagonal drawn out of rectangles is a
   * staircase; this is a real antialiased stroke, evaluated as a distance to
   * the segment in the fragment shader. One quad, whatever the angle.
   */
  drawLine(a: Point, b: Point, thickness: number, color: Color): void {
    const s = this.transform.scale;
    const half = Math.max(thickness, 0.1) / 2;
    // The quad is the segment's bounding box, grown by the stroke.
    const rect: Rect = {
      x: Math.min(a.x, b.x) - half,
      y: Math.min(a.y, b.y) - half,
      width: Math.abs(b.x - a.x) + half * 2,
      height: Math.abs(b.y - a.y) + half * 2,
    };
    const screen = this.rectToScreen(rect);
    // Endpoints as offsets from the quad center, matching `local` in the
    // shader — so they carry the transform's scale, as the radius does.
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;
    this.batcher.push(screen, {
      fill: color,
      mode: MODE_CAPSULE,
      cornerRadius: half * s,
      uv: [(a.x - cx) * s, (a.y - cy) * s, (b.x - cx) * s, (b.y - cy) * s],
    });
  }

  /**
   * A filled isosceles triangle inscribed in `r`, pointing `direction`.
   *
   * Its two sloping edges are antialiased by the same distance field the
   * rounded rects use. Built out of stacked rects it was a visible staircase,
   * which every drop-down arrow and scroll stepper in the framework wore.
   */
  fillTriangle(r: Rect, direction: Direction, color: Color, options: ShapeStyle = {}): void {
    this.batcher.push(this.rectToScreen(r), {
      fill: options.border ? TRANSPARENT_FILL : color,
      mode: MODE_TRIANGLE,
      uv: [DIRECTION_INDEX[direction], options.rotation ?? 0, 0, 0],
      ...borderOf(options, color, this.transform.scale),
    });
  }

  /**
   * A filled or outlined rhombus inscribed in `r` — its four edge midpoints.
   *
   * With `fillArc` giving wedges and `drawLine` giving strokes, the triangle
   * and this are what is left of the shape vocabulary a 2D canvas has. Neither
   * can be faked out of rectangles, and both take a rotation because a marker
   * that means "something is over there" has to point at it.
   */
  fillDiamond(r: Rect, color: Color, options: ShapeStyle = {}): void {
    this.batcher.push(this.rectToScreen(r), {
      fill: options.border ? TRANSPARENT_FILL : color,
      mode: MODE_DIAMOND,
      uv: [0, options.rotation ?? 0, 0, 0],
      ...borderOf(options, color, this.transform.scale),
    });
  }

  /**
   * A filled annular sector centered on `center`: the ring between
   * `innerRadius` and `outerRadius`, cut to the range `startAngle` to
   * `endAngle`. Angles are in radians clockwise from 12 o'clock, so a
   * cooldown that has run a quarter of its course is `0` to `Math.PI / 2`.
   *
   * Pass `innerRadius = 0` for a solid pie wedge. Like the capsule and the
   * triangle this is one instance evaluated as a distance field, which is the
   * only way to get a smooth curve out of a renderer with no rotation.
   */
  fillArc(
    center: Point,
    innerRadius: number,
    outerRadius: number,
    startAngle: number,
    endAngle: number,
    color: Color,
  ): void {
    if (!(outerRadius > 0) || startAngle === endAngle) return;
    const s = this.transform.scale;
    const r1 = outerRadius;
    const r0 = Math.min(Math.max(innerRadius, 0), r1);
    // A sweep past a full turn is the full ring; letting it through would
    // wrap the bisector round and cut the wedge from the wrong side.
    const span = Math.min(Math.abs(endAngle - startAngle), Math.PI * 2);
    const from = Math.min(startAngle, endAngle);
    const screen = this.rectToScreen({
      x: center.x - r1,
      y: center.y - r1,
      width: r1 * 2,
      height: r1 * 2,
    });
    // Radii go to the shader in the same screen pixels as `local`, so they
    // carry the transform's scale exactly as the capsule's half-thickness does.
    this.batcher.push(screen, {
      fill: color,
      mode: MODE_ARC,
      uv: [from, from + span, r0 * s, r1 * s],
    });
  }

  /**
   * A two-stop ramp out from `center`, given as a fraction of the rect.
   *
   * `radii` is how far the ramp reaches, also as a fraction, and defaults to
   * the half-diagonal so the far stop lands at the corners. Normalizing the
   * two axes separately makes it an ellipse that fills the rect, which is what
   * CSS's `radial-gradient(ellipse ...)` does and what a non-square panel
   * needs.
   */
  fillRadialGradient(
    r: Rect,
    from: Color,
    to: Color,
    center: readonly [number, number] = [0.5, 0.5],
    radii: readonly [number, number] = [0.5, 0.5],
    radius = 0,
  ): void {
    const s = this.transform.scale;
    this.batcher.push(this.rectToScreen(r), {
      fill: from,
      borderColor: to,
      uv: [center[0], center[1], radii[0], radii[1]],
      cornerRadius: radius * s,
      mode: MODE_RADIAL,
    });
  }

  /**
   * Light given off by a rounded rect: a halo `spread` px wide round `r`, full inside it and fading
   * to nothing at its edge. Drawn behind a control, it follows the control's own corners - where a
   * radial ramp is a round blob whatever the shape it sits behind.
   */
  fillGlow(r: Rect, color: Color, spread: number, radius = 0): void {
    if (spread <= 0 || color.a <= 0) return;
    const s = this.transform.scale;
    const grown = { x: r.x - spread, y: r.y - spread, width: r.width + spread * 2, height: r.height + spread * 2 };
    this.batcher.push(this.rectToScreen(grown), {
      fill: color,
      uv: [spread * s, 0, 0, 0],
      cornerRadius: radius * s,
      mode: MODE_GLOW,
    });
  }

  /**
   * A band of light crossing `r` on the diagonal, clipped to its rounded shape. `position` runs
   * from 0 (top-left) to 1 (bottom-right) along the diagonal - animate it past both ends to sweep
   * a shine across - and `width` is the band's half-width in the same units.
   */
  fillShine(r: Rect, color: Color, position: number, width = 0.12, radius = 0): void {
    if (color.a <= 0) return;
    const s = this.transform.scale;
    this.batcher.push(this.rectToScreen(r), {
      fill: color,
      uv: [position, width, 0, 0],
      cornerRadius: radius * s,
      mode: MODE_SHINE,
    });
  }

  /**
   * A pie wedge filling `r`, clipped to a rounded box of `cornerRadius`.
   *
   * Not `fillArc` with a radius: a cooldown sweeping over a square icon has to
   * reach the *corners*, so the sector's outer radius is the rect's
   * half-diagonal and the rounded box is what cuts it back. `start` and
   * `sweep` are radians clockwise from 12 o'clock, so a cooldown a quarter of
   * the way through is `(0, Math.PI / 2)`.
   */
  fillWedge(
    r: Rect,
    color: Color,
    start: number,
    sweep: number,
    cornerRadius = 0,
  ): void {
    if (sweep === 0) return;
    const s = this.transform.scale;
    const screen = this.rectToScreen(r);
    // Far enough to cover the corners of the rect it is being cut to.
    const outer = Math.hypot(screen.width, screen.height) * 0.5;
    const span = Math.min(Math.abs(sweep), Math.PI * 2);
    const from = sweep < 0 ? start + sweep : start;
    this.batcher.push(screen, {
      fill: color,
      mode: MODE_ARC,
      uv: [from, from + span, 0, outer],
      cornerRadius: cornerRadius * s,
    });
  }

  /** A full ring of `thickness` local px, centered on `center`. */
  strokeCircle(center: Point, radius: number, thickness: number, color: Color): void {
    const half = Math.max(thickness, 0.1) / 2;
    this.fillArc(center, radius - half, radius + half, 0, Math.PI * 2, color);
  }

  /** A filled disc. */
  fillCircle(center: Point, radius: number, color: Color): void {
    this.fillArc(center, 0, radius, 0, Math.PI * 2, color);
  }

  /**
   * Fill triangles, each `{ a, b, c, edges }` in local px, where `edges` says which sides lie on
   * the shape's outline (bit 0 a-b, bit 1 b-c, bit 2 c-a). Outline edges are antialiased and shared
   * ones are not, so a shape cut into triangles - by `triangulate`, by `strokeTriangles` - shows no
   * seams. One quad per triangle, the size of its bounding box.
   */
  fillTriangles(
    triangles: readonly { a: Point; b: Point; c: Point; edges?: number }[],
    color: Color,
  ): void {
    if (color.a <= 0) return;
    const t = this.transform;
    const s = t.scale;
    for (const tri of triangles) {
      const ax = tri.a.x * s + t.tx;
      const ay = tri.a.y * s + t.ty;
      const bx = tri.b.x * s + t.tx;
      const by = tri.b.y * s + t.ty;
      const cx = tri.c.x * s + t.tx;
      const cy = tri.c.y * s + t.ty;
      const x0 = Math.min(ax, bx, cx);
      const y0 = Math.min(ay, by, cy);
      const x1 = Math.max(ax, bx, cx);
      const y1 = Math.max(ay, by, cy);
      const w = Math.max(x1 - x0, 0.01);
      const h = Math.max(y1 - y0, 0.01);
      const mx = x0 + w / 2;
      const my = y0 + h / 2;
      this.batcher.push(
        { x: x0, y: y0, width: w, height: h },
        {
          fill: color,
          mode: MODE_POLY,
          uv: [ax - mx, ay - my, bx - mx, by - my],
          borderColor: { r: cx - mx, g: cy - my, b: tri.edges ?? 7, a: 0 },
        },
      );
    }
  }

  /** A run of connected line segments, stroked as one polyline. */
  drawPolyline(points: readonly Point[], thickness: number, color: Color): void {
    for (let i = 1; i < points.length; i++) {
      this.drawLine(points[i - 1]!, points[i]!, thickness, color);
    }
  }

  /** Horizontal line of `thickness` local px starting at (x, y). */
  hLine(x: number, y: number, length: number, color: Color, thickness = 1): void {
    this.fillRect({ x, y, width: length, height: thickness }, color);
  }

  /** Vertical line of `thickness` local px starting at (x, y). */
  vLine(x: number, y: number, length: number, color: Color, thickness = 1): void {
    this.fillRect({ x, y, width: thickness, height: length }, color);
  }

  // --- text -------------------------------------------------------------

  /**
   * Measure at layout scale (device pixel ratio only), so a string's size does
   * not shift when a ScrollViewer zooms.
   */
  measureText(text: string, font: FontSpec): TextMetrics {
    return this.text.measure(text, font, this.gpu.size.dpr);
  }

  /** `measureText(...).width` without building the metrics object. */
  measureWidth(text: string, font: FontSpec): number {
    return this.text.measureWidth(text, font, this.gpu.size.dpr);
  }

  /** Ellipsize to fit `maxWidth` logical px at layout scale. */
  ellipsize(text: string, font: FontSpec, maxWidth: number): string {
    return this.text.ellipsize(text, font, this.gpu.size.dpr, maxWidth);
  }

  /**
   * Draw a single line of text with its pen origin at (x, y) in local
   * coordinates. Glyphs are rasterized at the *current* transform scale, so
   * zoomed text stays crisp rather than being magnified from a smaller raster.
   */
  drawText(text: string, x: number, y: number, font: FontSpec, color: Color, opts: TextOptions = {}): number {
    if (text.length === 0 || this.isClippedOut) return 0;

    const dpr = this.gpu.size.dpr;
    const t = this.transform;
    const rasterScale = t.scale * dpr;
    const rasterPx = quantizeRasterSize(font.size, rasterScale);
    const atlas = this.atlas;
    const face = atlas.face(font, rasterPx);
    const metrics = face.metrics;
    // The raster was rounded onto the pixel grid, so every advance and metric that comes back
    // belongs to a font of `rasterPx` rather than to `font.size`. The *bitmaps* are wanted at
    // that size - it is what makes them crisp - but the pen is layout, and layout must not
    // depend on the scale a thing happens to be drawn at, or a run's width would change with
    // the zoom and text would overflow its box at some zooms and not others. So the advances
    // are scaled back to the size that was asked for, which is the conversion `TextLayout`
    // measures with; measured and painted are then the same number by construction.
    const advanceScale = (font.size * rasterScale) / rasterPx;

    let str = text;
    if (opts.maxWidth !== undefined) {
      str = this.text.ellipsize(text, font, dpr, opts.maxWidth);
      if (str.length === 0) return 0;
    }

    // Screen-space pen origin, in device pixels, snapped so atlas texels land
    // 1:1 on device pixels.
    const originX = x * t.scale + t.tx;
    const originY = y * t.scale + t.ty;
    let penDevice = Math.round(originX * dpr);
    let baselineDevice = Math.round(originY * dpr);

    // Reposition the baseline for the requested vertical anchor.
    switch (opts.baseline ?? "alphabetic") {
      case "top":
        baselineDevice = Math.round(originY * dpr + metrics.ascent * advanceScale);
        break;
      case "middle":
        baselineDevice = Math.round(
          originY * dpr +
            ((metrics.ascent + metrics.descent) / 2 - metrics.descent) * advanceScale,
        );
        break;
      case "bottom":
        baselineDevice = Math.round(originY * dpr - metrics.descent * advanceScale);
        break;
      case "alphabetic":
        break;
    }

    const startPen = penDevice;
    const batcher = this.batcher;
    // The page's texture, looked up only when the page changes.
    let page = -1;
    let pageTexture: Texture | null = null;
    for (let i = 0; i < str.length; ) {
      const code = codePointAt(str, i);
      i += code > 0xffff ? 2 : 1;
      const g = atlas.glyphOf(face, code);
      if (g.page >= 0) {
        if (g.page !== page) {
          page = g.page;
          pageTexture = atlas.texture(page);
        }
        // Snap each glyph to whole device pixels. The pen accumulates
        // fractional advances, so without this only the first character of a
        // run lands on the pixel grid and the rest resample.
        const gx = Math.round(penDevice + g.bearingX);
        const gy = Math.round(baselineDevice - g.bearingY);
        batcher.pushGlyph(
          gx / dpr,
          gy / dpr,
          g.width / dpr,
          g.height / dpr,
          color,
          g.u0,
          g.v0,
          g.u1,
          g.v1,
          pageTexture!,
        );
      }
      // Only the pen is scaled. `bearingX` and the quad below are the bitmap's own geometry at
      // `rasterPx`, and moving one without the other would slide the ink off the glyph box.
      penDevice += g.advance * advanceScale;
    }
    // Advance consumed, converted back to local units.
    return (penDevice - startPen) / (dpr * t.scale);
  }

  /** Baseline-to-baseline distance for `font`, in logical pixels. */
  lineHeight(font: FontSpec): number {
    // What `measureText(...).height` returns for any string, straight from the
    // face: measuring a probe string built a metrics object on every call.
    const rasterPx = quantizeRasterSize(font.size, this.gpu.size.dpr);
    const inv = font.size / rasterPx;
    return Math.ceil(this.atlas.face(font, rasterPx).metrics.lineHeight * inv * LINE_SPACING);
  }

  /**
   * Break `text` into lines no wider than `maxWidth` logical px.
   *
   * The rule itself is `wrapLines` in TextLayout; this binds it to the
   * current font and measurement scale.
   */
  wrapText(text: string, font: FontSpec, maxWidth: number): string[] {
    return wrapLines(text, maxWidth, (line: string) => this.measureWidth(line, font));
  }

  /**
   * Draw several lines inside `r`. Pass a string to have newlines honored,
   * or the array from `wrapText` when the caller has already wrapped and
   * measured.
   */
  drawTextBlock(
    text: string | readonly string[],
    r: Rect,
    font: FontSpec,
    color: Color,
    opts: TextBlockOptions = {},
  ): void {
    let lines = typeof text === "string" ? text.split("\n") : [...text];
    if (lines.length === 0) return;

    const step = opts.lineHeight ?? this.lineHeight(font);
    const limit = opts.maxLines ?? 0;
    let clipped = false;
    if (limit > 0 && lines.length > limit) {
      lines = lines.slice(0, limit);
      clipped = true;
    }
    // The last line kept stands in for everything dropped, so say so.
    if (clipped && lines.length > 0) {
      const last = lines.length - 1;
      lines[last] = this.ellipsize(`${lines[last]} ...`, font, r.width);
    }

    const blockHeight = lines.length * step;
    let top = r.y;
    if (opts.verticalAlign === "middle") top = r.y + (r.height - blockHeight) / 2;
    else if (opts.verticalAlign === "bottom") top = r.y + r.height - blockHeight;

    const align = opts.align ?? "left";
    lines.forEach((line, i) => {
      if (!line) return;
      let x = r.x;
      if (align !== "left") {
        const w = this.measureWidth(line, font);
        x = align === "center" ? r.x + (r.width - w) / 2 : r.x + r.width - w;
      }
      this.drawText(line, x, top + i * step, font, color, { baseline: "top" });
    });
  }

  /** Draw text centered inside `r` both horizontally and vertically. */
  drawTextCentered(text: string, r: Rect, font: FontSpec, color: Color): void {
    const m = this.measureText(text, font);
    this.drawText(
      text,
      r.x + (r.width - m.width) / 2,
      r.y + r.height / 2,
      font,
      color,
      { baseline: "middle" },
    );
  }

  /** Draw text left-aligned and vertically centered inside `r`. */
  drawTextInRect(text: string, r: Rect, font: FontSpec, color: Color, padX = 0): void {
    this.drawText(text, r.x + padX, r.y + r.height / 2, font, color, {
      baseline: "middle",
      maxWidth: Math.max(0, r.width - padX * 2),
    });
  }

  // --- gradients ---------------------------------------------------------

  /**
   * Fill a rounded rect with a linear gradient.
   *
   * `axis` gives the ramp's start and end in 0..1 quad space, so `[0,0,0,1]`
   * is top-to-bottom and `[0,0,1,1]` runs corner to corner. The whole thing is
   * one quad: the fragment shader interpolates, so there is no banding and no
   * extra draw calls.
   */
  fillGradient(
    r: Rect,
    from: Color,
    to: Color,
    axis: readonly [number, number, number, number] = GRADIENT_DOWN,
    radius = 0,
  ): void {
    const s = this.transform.scale;
    this.batcher.push(this.rectToScreen(r), {
      fill: from,
      borderColor: to,
      uv: axis,
      cornerRadius: radius * s,
      mode: MODE_GRADIENT,
    });
  }

  // --- rich text --------------------------------------------------------

  /** Advance of one line of spans, in local px. */
  measureSpans(spans: readonly TextSpan[], font: FontSpec): number {
    let total = 0;
    for (const span of spans) {
      total += span.text.length === 0
        ? (span.width ?? 0)
        : this.measureWidth(span.text, span.font ?? font);
    }
    return total;
  }

  /**
   * Wrap styled spans to `maxWidth` logical px, one array of spans per line.
   *
   * The rule is `wrapSpanRuns` in TextLayout, which defers to the same
   * break-offset code plain text uses; this binds it to the current
   * measurement scale and the fallback font.
   */
  wrapSpans(spans: readonly TextSpan[], font: FontSpec, maxWidth: number): TextSpan[][] {
    return wrapSpanRuns(
      spans,
      maxWidth,
      (span, from, to) =>
        span.text.length === 0
          ? (span.width ?? 0)
          : this.measureWidth(span.text.slice(from, to), span.font ?? font),
      (span, from, to) => ({ ...span, text: span.text.slice(from, to) }),
    );
  }

  /**
   * Draw one line of styled spans, with `y` at the *top* of the line rather
   * than on the baseline — every consumer of rich text (tooltips, chat,
   * dialogue) lays out by line box, and mixed font sizes have no one baseline
   * to share anyway. Returns the total advance in local px.
   */
  drawSpans(
    spans: readonly TextSpan[],
    x: number,
    y: number,
    font: FontSpec,
    color: Color,
    lineHeight?: number,
  ): number {
    const lh = lineHeight ?? this.lineHeight(font);
    let pen = x;
    for (const span of spans) {
      if (span.text.length === 0) {
        const width = span.width ?? 0;
        span.draw?.(this, { x: pen, y, width, height: lh });
        pen += width;
        continue;
      }
      pen += this.drawText(span.text, pen, y, span.font ?? font, span.color ?? color, {
        baseline: "top",
      });
    }
    return pen - x;
  }

  /**
   * Draw already-wrapped span lines inside `r`. The rich-text counterpart of
   * `drawTextBlock`; pass it what `wrapSpans` returned.
   */
  drawSpanBlock(
    lines: readonly (readonly TextSpan[])[],
    r: Rect,
    font: FontSpec,
    color: Color,
    opts: TextBlockOptions = {},
  ): void {
    if (lines.length === 0) return;
    const step = opts.lineHeight ?? this.lineHeight(font);
    const kept = opts.maxLines && opts.maxLines > 0 ? lines.slice(0, opts.maxLines) : lines;

    let top = r.y;
    const blockHeight = kept.length * step;
    if (opts.verticalAlign === "middle") top = r.y + (r.height - blockHeight) / 2;
    else if (opts.verticalAlign === "bottom") top = r.y + r.height - blockHeight;

    const align = opts.align ?? "left";
    kept.forEach((line, i) => {
      if (line.length === 0) return;
      let x = r.x;
      if (align !== "left") {
        const w = this.measureSpans(line, font);
        x = align === "center" ? r.x + (r.width - w) / 2 : r.x + r.width - w;
      }
      this.drawSpans(line, x, top + i * step, font, color, step);
    });
  }

  // --- images -----------------------------------------------------------

  /**
   * Draw a texture into `dest`. `src` selects a sub-rectangle in texture pixels;
   * omit it for the whole texture.
   */
  drawTexture(tex: DrawableTexture, dest: Rect, src?: Rect, tint?: Color): void {
    const uv: [number, number, number, number] = src
      ? [
          src.x / tex.width,
          src.y / tex.height,
          (src.x + src.width) / tex.width,
          (src.y + src.height) / tex.height,
        ]
      : [0, 0, 1, 1];
    this.batcher.push(this.rectToScreen(dest), {
      fill: tint ?? WHITE_TINT,
      mode: tex.premultiplied ? MODE_IMAGE_PREMULTIPLIED : MODE_IMAGE,
      uv,
      texture: tex.texture,
    });
  }
}

/** Gradient axes in 0..1 quad space, for `RenderContext.fillGradient`. */
export const GRADIENT_DOWN: readonly [number, number, number, number] = [0, 0, 0, 1];
export const GRADIENT_UP: readonly [number, number, number, number] = [0, 1, 0, 0];
export const GRADIENT_RIGHT: readonly [number, number, number, number] = [0, 0, 1, 0];
export const GRADIENT_LEFT: readonly [number, number, number, number] = [1, 0, 0, 0];
export const GRADIENT_DIAGONAL: readonly [number, number, number, number] = [0, 0, 1, 1];
